import { useSortable } from '@dnd-kit/sortable';
import { faEye, faEyeSlash, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, PropsWithChildren, useCallback } from 'react';

import useHasBeenInView from '@/hooks/useHasBeenInView';
import usePageAnchor from '@/hooks/usePageAnchor';
import { Props } from '@/pages/Profile/components/SortableSection/types';
import { AccordionContent, AccordionItem, AccordionTrigger, Button, Tooltip } from '@/ui';
import { cn } from '@/ui/lib/utils';

const SortableSection = ({
  actions,
  children,
  lazy = false,
  onClick,
  onHide,
  onUnhide,
  section
}: PropsWithChildren<Props>) => {
  const { defaultOpen, hidden, id, title } = section;
  // Hidden cards always sit at the bottom, so there is nowhere to drag them.
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    disabled: hidden,
    id
  });
  /*
   * Publishes the card to the person rail's "On this page" list. Keyed on the section id rather
   * than the title so the rail keeps its place through a rename, and read in document order, so
   * dragging a card also moves it in the rail.
   */
  const anchorRef = usePageAnchor(title, id);
  const [viewRef, hasBeenInView] = useHasBeenInView<HTMLDivElement>();
  const isHeldBack = lazy && !hasBeenInView;
  const sortableAndViewRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      viewRef(node);
    },
    [setNodeRef, viewRef]
  );

  return (
    <div className="mb-2" id={section.id} ref={anchorRef}>
      <div
        className="relative"
        ref={sortableAndViewRef}
        style={{
          transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
          transition
        }}>
        <AccordionItem
          className={cn(
            'group/section bg-white dark:bg-uniform-gray-900',
            hidden && 'border-dashed bg-uniform-gray-50 dark:bg-uniform-gray-800/40'
          )}
          defaultOpen={defaultOpen}>
          {/* A size container, so actions can drop detail the header has no room for */}
          <div className="@container flex flex-wrap items-center">
            {/* Never narrower than the title, so the actions wrap below it instead of hiding it */}
            <div className="flex max-w-full min-w-0 flex-[1_0_auto] items-center">
              {hidden ? (
                <span className="self-stretch pl-3 pr-1" />
              ) : (
                <button
                  aria-label={`Drag ${id}`}
                  className={`self-stretch pl-3 pr-1 text-uniform-gray-400 hover:text-uniform-gray-600 ${
                    isDragging ? 'cursor-grabbing' : 'cursor-grab'
                  }`}
                  // On the handle, not the section: they make it role="button", which
                  // gave the whole section a pointer cursor
                  {...attributes}
                  {...listeners}
                  type="button">
                  <FontAwesomeIcon icon={faGripVertical} className="text-sm" />
                </button>
              )}

              <AccordionTrigger
                className={cn(
                  'min-w-0 flex-1 rounded-lg pl-2 font-semibold',
                  'hover:bg-uniform-gray-100/70 dark:hover:bg-uniform-gray-800/60',
                  hidden && 'text-uniform-gray-500'
                )}
                iconPlacement="inline"
                onClick={() => onClick(section.id)}>
                {title}
              </AccordionTrigger>
            </div>

            {(actions || onHide || onUnhide) && (
              <div className="ml-auto flex shrink-0 items-center gap-2 pl-2 pr-3">
                {!hidden && actions}

                {hidden && onUnhide && (
                  <Button
                    aria-label={`Unhide ${title}`}
                    color="neutral"
                    leftIcon={faEye}
                    size="xs"
                    variant="outline"
                    onClick={() => onUnhide(id)}>
                    Unhide
                  </Button>
                )}

                {/*
                 * Hiding is a layout choice rather than something done to the member, so it
                 * stays out of the way of the card's own actions: last in the row, and only
                 * surfaced on hover or focus where there is a pointer to hover with.
                 */}
                {!hidden && onHide && (
                  <Tooltip content="Hide this card">
                    <Button
                      aria-label={`Hide ${title}`}
                      className="opacity-0 transition-opacity group-hover/section:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
                      color="neutral"
                      size="icon-xs"
                      variant="ghost"
                      onClick={() => onHide(id)}>
                      <FontAwesomeIcon icon={faEyeSlash} />
                    </Button>
                  </Tooltip>
                )}
              </div>
            )}
          </div>

          <AccordionContent className="border-t-0 pt-0">
            {isHeldBack ? <div className="min-h-24" data-testid="section-placeholder" /> : children}
          </AccordionContent>
        </AccordionItem>
      </div>
    </div>
  );
};

export default memo(SortableSection);
